import {
  useCreateAutoAnnotateBatchMutation,
  useGetActiveAutoAnnotateBatchQuery,
  useRunVisionDetectionMutation,
} from "@/modules/label/services/labelApi";
import {
  AutoAnnotationBatch,
  AutoAnnotationBatchItemStatusEnum,
  AutoAnnotationBatchStatusEnum,
  CreateAutoAnnotationBatchResponse,
  PreAnnotateModelTypeEnum,
  RunVisionRequest,
} from "@repo/schema";
import { useCallback, useEffect, useMemo, useRef } from "react";

/** How often the live batch is re-read while one is running. */
export const BATCH_POLL_MS = 5_000;

/**
 * Pacing applied only AFTER the first 429.
 *
 * The fan-out is unbounded by design — every selected image is dispatched at
 * once, which is what makes a batch finish in one image's wall-clock rather
 * than N. But Vertex 429s freely on this project, and an unbounded retry
 * against a saturated quota is just the same storm again. So the first
 * rejection collapses whatever has NOT been dispatched yet into a small paced
 * window: full speed while the quota allows it, survivable when it does not.
 */
const THROTTLED_CONCURRENCY = 4;
const BACKOFF_BASE_MS = 2_000;
const BACKOFF_MAX_MS = 30_000;
const MAX_ATTEMPTS = 3;

/** HTTP statuses worth another attempt: quota, and the gateway's own timeouts. */
const RETRYABLE = new Set([429, 502, 503, 504]);

const statusOf = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown })?.status;
  return typeof status === "number" ? status : undefined;
};

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

/** Exponential with jitter — a synchronised retry is a thundering herd. */
const backoffFor = (attempt: number): number =>
  Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_MAX_MS) * (0.5 + Math.random());

export interface BatchAutoAnnotate {
  /** The live batch, or the one that just finished. Null when nothing is running. */
  batch: AutoAnnotationBatch | null;
  /** Creating the batch record — the brief wait between Run and the first dispatch. */
  isStarting: boolean;
  /** Per-image state, indexed for the Data source rows. */
  itemStatusByTaskId: Map<number, AutoAnnotationBatchItemStatusEnum>;
  start: (taskIds: number[]) => Promise<CreateAutoAnnotationBatchResponse>;
}

/**
 * Drive a batch auto-annotation from the browser.
 *
 * The split of responsibilities is deliberate and not obvious: the API owns
 * the batch RECORD, this hook owns DISPATCH. The API runs with `cpu_idle`, so
 * work started after a response is flushed may never finish — a server-driven
 * batch would need a Cloud Run Job. Meanwhile the per-image endpoints already
 * stage their results, so firing them from here loses nothing: closing the tab
 * costs the progress view, not the annotations.
 */
export const useBatchAutoAnnotate = (
  projectId: number | undefined,
  onSettled?: () => void,
): BatchAutoAnnotate => {
  const [createBatch, { isLoading: isStarting }] = useCreateAutoAnnotateBatchMutation();
  const [runVisionDetection] = useRunVisionDetectionMutation();

  const result = useGetActiveAutoAnnotateBatchQuery(
    { projectId: projectId! },
    { skip: !projectId },
  );
  const batch = result.data?.batch ?? null;
  const isRunning = batch?.status === AutoAnnotationBatchStatusEnum.RUNNING;

  // Second subscription to the same cache entry, exactly as useConfidenceReport
  // does: a hook cannot derive its own pollingInterval from its own result, and
  // RTK Query polls at the shortest interval among active subscribers.
  useGetActiveAutoAnnotateBatchQuery(
    { projectId: projectId! },
    { skip: !projectId || !isRunning, pollingInterval: BATCH_POLL_MS },
  );

  const abortRef = useRef<AbortController | null>(null);
  // Abort dispatch on unmount only. Navigating away does NOT cancel the run —
  // requests already in flight finish server-side and stage their boxes either
  // way — it just stops this tab queueing more after a backoff.
  useEffect(() => () => abortRef.current?.abort(), []);

  // One refetch of the task list when the batch ends, replacing the forty the
  // per-run invalidation would otherwise have fired. See invalidateAfterRun.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (isRunning) wasRunning.current = true;
    else if (wasRunning.current) {
      wasRunning.current = false;
      onSettled?.();
    }
  }, [isRunning, onSettled]);

  const start = useCallback(
    async (taskIds: number[]): Promise<CreateAutoAnnotationBatchResponse> => {
      if (!projectId) throw new Error("no project");
      const created = await createBatch({ projectId, body: { taskIds } }).unwrap();
      const accepted = created.batch.items.map((item) => item.taskId);

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      // Not awaited: Run returns as soon as the record exists, so the modal
      // closes and the progress card takes over while the images are in
      // flight. Failures are recorded server-side per image, so there is
      // nothing here for a caller to catch.
      void dispatchAll({
        batchId: created.batch.id,
        projectId,
        taskIds: accepted,
        modelType: created.batch.modelType,
        signal: controller.signal,
        runVision: (args) => runVisionDetection(args).unwrap(),
      });

      return created;
    },
    [createBatch, projectId, runVisionDetection],
  );

  // Memoised because it is a prop of the Data source list: rebuilding it every
  // render would re-render fifty rows on every unrelated state change, and the
  // poll already gives it a new identity every five seconds.
  const itemStatusByTaskId = useMemo(
    () => new Map((batch?.items ?? []).map((item) => [item.taskId, item.status])),
    [batch],
  );

  return { batch, isStarting, itemStatusByTaskId, start };
};

interface DispatchArgs {
  batchId: number;
  projectId: number;
  taskIds: number[];
  modelType: PreAnnotateModelTypeEnum;
  signal: AbortSignal;
  runVision: (args: {
    projectId: number;
    taskId: number;
    body: RunVisionRequest;
  }) => Promise<unknown>;
}

/**
 * Fire every image at once, then mop up whatever the quota refused.
 *
 * The first pass is deliberately unbounded: that is what makes a batch cost
 * one image's wall-clock instead of N. What the first 429 changes is the
 * SECOND pass — by then the only work left is the set that was rejected, and
 * re-firing that set all at once against a quota that just said no would
 * reproduce the same wall. So retries run through a small paced window with
 * exponential backoff instead.
 *
 * Nothing here reports failures. A run that reached the API marked its own
 * item; a request that never arrived leaves the item PENDING, which the
 * batch's liveness rule resolves as failed. That is also what covers the tab
 * being closed halfway through.
 *
 * Exported for tests: the adaptive behaviour is the point of this module and
 * is not observable through the hook without a live server.
 */
export async function dispatchAll(args: DispatchArgs): Promise<void> {
  const { taskIds, signal } = args;

  let queue = await unboundedPass(args, taskIds);

  for (let attempt = 1; attempt < MAX_ATTEMPTS; attempt++) {
    if (queue.length === 0 || signal.aborted) return;
    await sleep(backoffFor(attempt - 1), signal);
    if (signal.aborted) return;
    queue = await pacedPass(args, queue);
  }
}

/** Everything at once. Returns the images worth another attempt. */
async function unboundedPass(
  args: DispatchArgs,
  taskIds: number[],
): Promise<number[]> {
  const settled = await Promise.allSettled(
    taskIds.map((taskId) => runOne(args, taskId)),
  );
  return taskIds.filter((_, i) => isRetryable(settled[i]));
}

/** The same work through a window of THROTTLED_CONCURRENCY at a time. */
async function pacedPass(args: DispatchArgs, taskIds: number[]): Promise<number[]> {
  const retry: number[] = [];
  for (let i = 0; i < taskIds.length; i += THROTTLED_CONCURRENCY) {
    if (args.signal.aborted) break;
    const window = taskIds.slice(i, i + THROTTLED_CONCURRENCY);
    const settled = await Promise.allSettled(
      window.map((taskId) => runOne(args, taskId)),
    );
    window.forEach((taskId, j) => {
      if (isRetryable(settled[j])) retry.push(taskId);
    });
  }
  return retry;
}

/**
 * Worth another attempt?
 *
 * Only transport-level refusals — quota and gateway timeouts. A 400 (the
 * project is misconfigured) or a 409 (something else is running on this image)
 * would fail identically every time, and a 502 from an unparseable model
 * response is the one genuine judgement call here: it is retried because the
 * model is non-deterministic, so the next attempt may well parse.
 */
function isRetryable(settled: PromiseSettledResult<unknown> | undefined): boolean {
  if (!settled || settled.status === "fulfilled") return false;
  const status = statusOf(settled.reason);
  return status !== undefined && RETRYABLE.has(status);
}

/**
 * One image through Robopipe Vision, the only engine a batch can run. A batch
 * is created and driven in the same breath, so `start` never dispatches a
 * historic batch that recorded another provider.
 */
function runOne(args: DispatchArgs, taskId: number): Promise<unknown> {
  const { batchId, projectId, modelType } = args;
  return args.runVision({
    projectId,
    taskId,
    body: { modelType, batchId },
  });
}
